import React, { useEffect, useState } from "react";
import {
  Table,
  DatePicker,
  Select,
  Input,
  Button,
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Space,
  Descriptions,
  Divider,
  Progress,
  Alert,
} from "antd";
import {
  DownloadOutlined,
  SearchOutlined,
  CalendarOutlined,
  UserOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { isDropdownPermission } from "../../../Hooks/permissionsMiddleware";
import {
  getClientOutstanding,
  fetchGuestHouseOutlets,
} from "../../../Store/Apis/ReservationApi";
import { FaFileExcel, FaRegFilePdf } from "react-icons/fa";
import downloadPDF from "../../../Hooks/downloadReportPdf";

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-ZA", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

// Helper function to calculate days overdue
const calculateDaysOverdue = (dueDate) => {
  const today = new Date();
  const due = new Date(dueDate);
  const diffTime = today - due;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : 0;
};

// Helper function to get status color
const getStatusColor = (daysOverdue) => {
  if (daysOverdue <= 0) return "green";
  if (daysOverdue <= 30) return "orange";
  if (daysOverdue <= 60) return "red";
  return "volcano";
};

// Helper function to get status text
const getStatusText = (daysOverdue) => {
  if (daysOverdue <= 0) return "Current";
  if (daysOverdue <= 30) return "1-30 Days";
  if (daysOverdue <= 60) return "31-60 Days";
  return "Over 90 Days";
};

const CreditManagerReport = () => {
  const user = useSelector((state) => state.auth.user);
  const today = new Date();

  const [dateRange, setDateRange] = useState(null);
  const [outletFilter, setOutletFilter] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState("customer");

  // Live data from API
  const [apiData, setApiData] = useState({
    agingSummaries: [],
    orderDetails: [],
  });
  const [loadingData, setLoadingData] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    setLoadingData(true);
    getClientOutstanding()
      .then((res) => {
        if (res?.status === 200 && res.data) {
          setApiData({
            agingSummaries: res.data.agingSummaries || [],
            orderDetails: res.data.orderDetails || [],
          });
        } else {
          setLoadError("Failed to load credit data");
        }
      })
      .catch(() => setLoadError("Failed to load credit data"))
      .finally(() => setLoadingData(false));
  }, []);

  // Enhanced mock data with multiple invoices per customer
  const mockInvoiceData = [
    // John Smith - Multiple invoices
    {
      key: "inv1",
      customerId: "CUST001",
      customerName: "John Smith",
      customerPhone: "+27 11 123 4567",
      customerEmail: "john.smith@email.com",
      creditLimit: 10000.0,
      invoiceNumber: "INV-2025-001",
      invoiceDate: "2025-08-15",
      checkOutDate: "2025-08-15",
      dueDate: "2025-08-25",
      invoiceAmount: 1500.0,
      outstandingBalance: 750.0,
      daysOverdue: 5,
      paymentDetails: [
        { payAmount: 750.0, paymentMode: "Cash", date: "2025-08-20" },
      ],
    },
    {
      key: "inv2",
      customerId: "CUST001",
      customerName: "John Smith",
      customerPhone: "+27 11 123 4567",
      customerEmail: "john.smith@email.com",
      creditLimit: 10000.0,
      invoiceNumber: "INV-2025-005",
      invoiceDate: "2025-08-10",
      checkOutDate: "2025-08-10",
      dueDate: "2025-08-20",
      invoiceAmount: 2200.0,
      outstandingBalance: 2200.0,
      daysOverdue: 10,
      paymentDetails: [],
    },
    {
      key: "inv3",
      customerId: "CUST001",
      customerName: "John Smith",
      customerPhone: "+27 11 123 4567",
      customerEmail: "john.smith@email.com",
      creditLimit: 10000.0,
      invoiceNumber: "INV-2025-012",
      invoiceDate: "2025-08-05",
      checkOutDate: "2025-08-05",
      dueDate: "2025-08-15",
      invoiceAmount: 1800.0,
      outstandingBalance: 900.0,
      daysOverdue: 15,
      paymentDetails: [
        { payAmount: 900.0, paymentMode: "Bank Transfer", date: "2025-08-12" },
      ],
    },
    // Sarah Johnson - High risk customer
    {
      key: "inv4",
      customerId: "CUST002",
      customerName: "Sarah Johnson",
      customerPhone: "+27 21 987 6543",
      customerEmail: "sarah.johnson@email.com",
      creditLimit: 5000.0,
      invoiceNumber: "INV-2025-002",
      invoiceDate: "2025-07-10",
      checkOutDate: "2025-07-10",
      dueDate: "2025-07-20",
      invoiceAmount: 2300.0,
      outstandingBalance: 2300.0,
      daysOverdue: 31,
      paymentDetails: [],
    },
    {
      key: "inv5",
      customerId: "CUST002",
      customerName: "Sarah Johnson",
      customerPhone: "+27 21 987 6543",
      customerEmail: "sarah.johnson@email.com",
      creditLimit: 5000.0,
      invoiceNumber: "INV-2025-008",
      invoiceDate: "2025-07-25",
      checkOutDate: "2025-07-25",
      dueDate: "2025-08-05",
      invoiceAmount: 1800.0,
      outstandingBalance: 1800.0,
      daysOverdue: 17,
      paymentDetails: [],
    },
    // Mike Wilson - Over credit limit
    {
      key: "inv6",
      customerId: "CUST003",
      customerName: "Mike Wilson",
      customerPhone: "+27 31 555 9876",
      customerEmail: "mike.wilson@email.com",
      creditLimit: 3000.0,
      invoiceNumber: "INV-2025-003",
      invoiceDate: "2025-05-01",
      checkOutDate: "2025-05-01",
      dueDate: "2025-05-15",
      invoiceAmount: 5400.0,
      outstandingBalance: 3200.0,
      daysOverdue: 97,
      paymentDetails: [
        { payAmount: 1200.0, paymentMode: "Bank Transfer", date: "2025-06-01" },
        { payAmount: 1000.0, paymentMode: "Cash", date: "2025-07-15" },
      ],
    },
    // Lisa Chen - Good customer
    {
      key: "inv7",
      customerId: "CUST004",
      customerName: "Lisa Chen",
      customerPhone: "+27 11 444 3333",
      customerEmail: "lisa.chen@email.com",
      creditLimit: 8000.0,
      invoiceNumber: "INV-2025-004",
      invoiceDate: "2025-08-18",
      checkOutDate: "2025-08-18",
      dueDate: "2025-09-01",
      invoiceAmount: 890.0,
      outstandingBalance: 445.0,
      daysOverdue: 0,
      paymentDetails: [
        { payAmount: 445.0, paymentMode: "Credit Card", date: "2025-08-19" },
      ],
    },
  ];

  // Dynamic outlets
  const [outlets, setOutlets] = useState([]);
  const [loadingOutlets, setLoadingOutlets] = useState(false);
  useEffect(() => {
    setLoadingOutlets(true);
    fetchGuestHouseOutlets()
      .then((data) => setOutlets(Array.isArray(data) ? data : []))
      .catch(() => setOutlets([]))
      .finally(() => setLoadingOutlets(false));
  }, []);

  // Transform and filter data
  const processedData = React.useMemo(() => {
    // Build client name lookup from aging summaries
    const nameLookup = new Map();
    (apiData.agingSummaries || []).forEach((a) => {
      const key = String(a.clientID);
      const val = a.clientName || a.clientname || a.name;
      if (val) nameLookup.set(key, val);
    });

    // Map API order details into the invoice rows expected by the table
    const mapped = (apiData.orderDetails || []).map((o) => {
      // derive due date by adding Net days (e.g., "Net 30") to checkOutDate/orderDate
      const baseDateStr = o.checkOutDate || o.orderDate;
      const base = baseDateStr ? new Date(baseDateStr) : null;
      const termNum =
        typeof o.paymentTerms === "string"
          ? parseInt(o.paymentTerms.replace(/\D/g, ""), 10) || 0
          : 0;
      let dueDate = base ? new Date(base) : null;
      if (dueDate && termNum > 0) {
        dueDate.setDate(dueDate.getDate() + termNum);
      }
      const dueDateStr = dueDate ? dueDate.toISOString() : baseDateStr;

      const customerId = String(o.clientID);
      const displayName =
        nameLookup.get(customerId) ||
        o.clientName ||
        o.name ||
        `Client ${customerId}`;

      return {
        key: String(o.orderID || o.orderNumber || o.utrNumber || Math.random()),
        customerId,
        customerName: displayName,
        customerPhone: o.phone || "",
        customerEmail: o.email || "",
        creditLimit: Number(o.creditLimit || 0),
        invoiceNumber: o.utrNumber || o.orderNumber || String(o.orderID),
        invoiceDate: o.orderDate,
        checkOutDate: o.checkOutDate,
        dueDate: dueDateStr,
        invoiceAmount: Number(o.totalOrderAmount || 0),
        outstandingBalance: Number(o.outstandingAmount || 0),
        totalReceivedAmount: Number(o.totalReceivedAmount || 0),
        daysOverdue: calculateDaysOverdue(dueDateStr),
        paymentDetails: [],
      };
    });

    let filteredData = mapped;

    // Filter by date range
    if (dateRange && dateRange.length === 2) {
      const [startDate, endDate] = dateRange;
      filteredData = filteredData.filter((item) => {
        const checkOutDate = new Date(item.checkOutDate);
        return (
          checkOutDate >= startDate.toDate() && checkOutDate <= endDate.toDate()
        );
      });
    }

    // Filter by customer
    if (customerFilter) {
      filteredData = filteredData.filter(
        (item) =>
          item.customerName
            .toLowerCase()
            .includes(customerFilter.toLowerCase()) ||
          item.customerPhone.includes(customerFilter) ||
          item.invoiceNumber
            .toLowerCase()
            .includes(customerFilter.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter) {
      filteredData = filteredData.filter((item) => {
        const status = getStatusText(item.daysOverdue);
        return status === statusFilter;
      });
    }

    return filteredData;
  }, [dateRange, customerFilter, statusFilter, apiData]);

  // Group data by customer for customer view
  const customerGroupedData = React.useMemo(() => {
    const grouped = processedData.reduce((acc, invoice) => {
      const customerId = invoice.customerId;
      if (!acc[customerId]) {
        acc[customerId] = {
          key: customerId,
          customerId,
          customerName: invoice.customerName,
          customerPhone: invoice.customerPhone,
          customerEmail: invoice.customerEmail,
          creditLimit: invoice.creditLimit,
          invoices: [],
          totalOutstanding: 0,
          totalInvoiced: 0,
          oldestDueDate: null,
          maxDaysOverdue: 0,
          invoiceCount: 0,
        };
      }

      acc[customerId].invoices.push(invoice);
      acc[customerId].totalOutstanding += invoice.outstandingBalance;
      acc[customerId].totalInvoiced += invoice.invoiceAmount;
      acc[customerId].invoiceCount += 1;
      acc[customerId].maxDaysOverdue = Math.max(
        acc[customerId].maxDaysOverdue,
        invoice.daysOverdue
      );

      if (
        !acc[customerId].oldestDueDate ||
        new Date(invoice.dueDate) < new Date(acc[customerId].oldestDueDate)
      ) {
        acc[customerId].oldestDueDate = invoice.dueDate;
      }

      return acc;
    }, {});

    return Object.values(grouped).sort(
      (a, b) => b.totalOutstanding - a.totalOutstanding
    );
  }, [processedData]);

  // Customer view columns
  const customerColumns = [
    {
      title: "Customer Details",
      key: "customerDetails",
      width: 250,
      render: (_, record) => (
        <div>
          <Text strong style={{ display: "block" }}>
            {record.customerName}
          </Text>
          <Text type="secondary" style={{ fontSize: "12px", display: "block" }}>
            {record.customerPhone}
          </Text>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {record.customerEmail}
          </Text>
        </div>
      ),
    },
    {
      title: "Credit Info",
      key: "creditInfo",
      width: 180,
      render: (_, record) => {
        const utilizationPercent =
          (record.totalOutstanding / record.creditLimit) * 100;
        const isOverLimit = record.totalOutstanding > record.creditLimit;

        return (
          <div>
            <Text style={{ display: "block", fontSize: "12px" }}>
              Limit: <Text strong>R {record.creditLimit.toFixed(2)}</Text>
            </Text>
            <Text style={{ display: "block", fontSize: "12px" }}>
              Used:{" "}
              <Text
                strong
                style={{ color: isOverLimit ? "#ff4d4f" : "#1890ff" }}
              >
                R {record.totalOutstanding.toFixed(2)}
              </Text>
            </Text>
            <Progress
              percent={Math.min(utilizationPercent, 100)}
              size="small"
              strokeColor={
                isOverLimit
                  ? "#ff4d4f"
                  : utilizationPercent > 80
                  ? "#faad14"
                  : "#52c41a"
              }
              showInfo={false}
              style={{ marginTop: 4 }}
            />
            {isOverLimit && (
              <Tag color="red" size="small">
                Over Limit
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: "Outstanding Amount",
      dataIndex: "totalOutstanding",
      key: "totalOutstanding",
      width: 150,
      render: (amount, record) => (
        <div>
          <Text
            strong
            style={{
              color: amount > 0 ? "#ff4d4f" : "#52c41a",
              fontSize: "16px",
              display: "block",
            }}
          >
            R {amount.toFixed(2)}
          </Text>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {record.invoiceCount} invoice{record.invoiceCount !== 1 ? "s" : ""}
          </Text>
        </div>
      ),
      sorter: (a, b) => a.totalOutstanding - b.totalOutstanding,
    },
    {
      title: "Risk Status",
      key: "riskStatus",
      width: 120,
      render: (_, record) => {
        const isOverLimit = record.totalOutstanding > record.creditLimit;
        const utilizationPercent =
          (record.totalOutstanding / record.creditLimit) * 100;

        let status = "Low Risk";
        let color = "green";

        if (isOverLimit) {
          status = "Over Limit";
          color = "red";
        } else if (record.maxDaysOverdue > 60) {
          status = "High Risk";
          color = "volcano";
        } else if (record.maxDaysOverdue > 30 || utilizationPercent > 80) {
          status = "Medium Risk";
          color = "orange";
        }

        return <Tag color={color}>{status}</Tag>;
      },
      filters: [
        { text: "Low Risk", value: "Low Risk" },
        { text: "Medium Risk", value: "Medium Risk" },
        { text: "High Risk", value: "High Risk" },
        { text: "Over Limit", value: "Over Limit" },
      ],
      onFilter: (value, record) => {
        const isOverLimit = record.totalOutstanding > record.creditLimit;
        const utilizationPercent =
          (record.totalOutstanding / record.creditLimit) * 100;

        let status = "Low Risk";
        if (isOverLimit) {
          status = "Over Limit";
        } else if (record.maxDaysOverdue > 60) {
          status = "High Risk";
        } else if (record.maxDaysOverdue > 30 || utilizationPercent > 80) {
          status = "Medium Risk";
        }

        return status === value;
      },
    },
    {
      title: "Days Overdue",
      dataIndex: "maxDaysOverdue",
      key: "maxDaysOverdue",
      width: 120,
      render: (days) => (
        <Tag color={getStatusColor(days)}>
          {days > 0 ? `${days} days` : "Current"}
        </Tag>
      ),
      sorter: (a, b) => a.maxDaysOverdue - b.maxDaysOverdue,
    },
  ];

  // Invoice view columns (original with enhancements)
  const invoiceColumns = [
    {
      title: "Customer Name",
      dataIndex: "customerName",
      key: "customerName",
      width: 150,
      sorter: (a, b) => a.customerName.localeCompare(b.customerName),
    },
    {
      title: "Invoice Number",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      width: 130,
    },
    {
      title: "Invoice Date",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      width: 120,
      render: (date) => formatDate(date),
      sorter: (a, b) => new Date(a.invoiceDate) - new Date(b.invoiceDate),
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      key: "dueDate",
      width: 120,
      render: (date) => formatDate(date),
      sorter: (a, b) => new Date(a.dueDate) - new Date(b.dueDate),
    },
    {
      title: "Invoice Amount",
      dataIndex: "invoiceAmount",
      key: "invoiceAmount",
      width: 130,
      render: (amount) => `R ${amount.toFixed(2)}`,
      sorter: (a, b) => a.invoiceAmount - b.invoiceAmount,
    },
    {
      title: "Outstanding Balance",
      dataIndex: "outstandingBalance",
      key: "outstandingBalance",
      width: 150,
      render: (amount) => (
        <Text strong style={{ color: amount > 0 ? "#ff4d4f" : "#52c41a" }}>
          R {amount.toFixed(2)}
        </Text>
      ),
      sorter: (a, b) => a.outstandingBalance - b.outstandingBalance,
    },
    {
      title: "Days Overdue",
      dataIndex: "daysOverdue",
      key: "daysOverdue",
      width: 120,
      render: (days) => (
        <Tag color={getStatusColor(days)}>
          {days > 0 ? `${days} days` : "Current"}
        </Tag>
      ),
      sorter: (a, b) => a.daysOverdue - b.daysOverdue,
    },
    {
      title: "Status",
      key: "status",
      width: 110,
      render: (_, record) => {
        const status = getStatusText(record.daysOverdue);
        return <Tag color={getStatusColor(record.daysOverdue)}>{status}</Tag>;
      },
      filters: [
        { text: "Current", value: "Current" },
        { text: "1-30 Days", value: "1-30 Days" },
        { text: "31-60 Days", value: "31-60 Days" },
        { text: "Over 90 Days", value: "Over 90 Days" },
      ],
      onFilter: (value, record) => getStatusText(record.daysOverdue) === value,
    },
  ];

  // Customer expandable row render
  const customerExpandedRowRender = (record) => {
    const agingData = record.invoices.map((invoice) => ({
      ...invoice,
      current: invoice.daysOverdue <= 0 ? invoice.outstandingBalance : 0,
      thirtyDays:
        invoice.daysOverdue > 0 && invoice.daysOverdue <= 30
          ? invoice.outstandingBalance
          : 0,
      sixtyDays:
        invoice.daysOverdue > 30 && invoice.daysOverdue <= 60
          ? invoice.outstandingBalance
          : 0,
      over90Days: invoice.daysOverdue > 60 ? invoice.outstandingBalance : 0,
    }));

    const totals = agingData.reduce(
      (acc, item) => ({
        current: acc.current + item.current,
        thirtyDays: acc.thirtyDays + item.thirtyDays,
        sixtyDays: acc.sixtyDays + item.sixtyDays,
        over90Days: acc.over90Days + item.over90Days,
      }),
      { current: 0, thirtyDays: 0, sixtyDays: 0, over90Days: 0 }
    );

    return (
      <div style={{ padding: "16px", backgroundColor: "#fafafa" }}>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={12}>
            <Title level={5}>Customer Summary</Title>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Total Invoices">
                {record.invoiceCount}
              </Descriptions.Item>
              <Descriptions.Item label="Total Invoiced Amount">
                R {record.totalInvoiced.toFixed(2)}
              </Descriptions.Item>
              <Descriptions.Item label="Credit Limit">
                R {record.creditLimit.toFixed(2)}
              </Descriptions.Item>
              <Descriptions.Item label="Available Credit">
                <Text
                  style={{
                    color:
                      record.creditLimit - record.totalOutstanding < 0
                        ? "#ff4d4f"
                        : "#52c41a",
                  }}
                >
                  R {(record.creditLimit - record.totalOutstanding).toFixed(2)}
                </Text>
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col span={12}>
            <Title level={5}>Aging Summary</Title>
            <Row gutter={8}>
              <Col span={6}>
                <Card
                  size="small"
                  style={{ textAlign: "center", border: "1px solid #52c41a" }}
                >
                  <Text style={{ fontSize: "12px", color: "#52c41a" }}>
                    Current
                  </Text>
                  <div>
                    <Text strong style={{ color: "#52c41a" }}>
                      R {totals.current.toFixed(2)}
                    </Text>
                  </div>
                </Card>
              </Col>
              <Col span={6}>
                <Card
                  size="small"
                  style={{ textAlign: "center", border: "1px solid #faad14" }}
                >
                  <Text style={{ fontSize: "12px", color: "#faad14" }}>
                    30 Days
                  </Text>
                  <div>
                    <Text strong style={{ color: "#faad14" }}>
                      R {totals.thirtyDays.toFixed(2)}
                    </Text>
                  </div>
                </Card>
              </Col>
              <Col span={6}>
                <Card
                  size="small"
                  style={{ textAlign: "center", border: "1px solid #ff7a45" }}
                >
                  <Text style={{ fontSize: "12px", color: "#ff7a45" }}>
                    60 Days
                  </Text>
                  <div>
                    <Text strong style={{ color: "#ff7a45" }}>
                      R {totals.sixtyDays.toFixed(2)}
                    </Text>
                  </div>
                </Card>
              </Col>
              <Col span={6}>
                <Card
                  size="small"
                  style={{ textAlign: "center", border: "1px solid #ff4d4f" }}
                >
                  <Text style={{ fontSize: "12px", color: "#ff4d4f" }}>
                    90+ Days
                  </Text>
                  <div>
                    <Text strong style={{ color: "#ff4d4f" }}>
                      R {totals.over90Days.toFixed(2)}
                    </Text>
                  </div>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>

        <Divider />

        <Title level={5}>Invoice Details</Title>
        <Table
          columns={[
            {
              title: "Invoice #",
              dataIndex: "invoiceNumber",
              width: 120,
            },
            {
              title: "Date",
              dataIndex: "invoiceDate",
              width: 100,
              render: (date) => formatDate(date),
            },
            {
              title: "Due Date",
              dataIndex: "dueDate",
              width: 100,
              render: (date) => formatDate(date),
            },
            {
              title: "Amount",
              dataIndex: "invoiceAmount",
              width: 100,
              render: (amount) => `R ${amount.toFixed(2)}`,
            },
            {
              title: "Outstanding",
              dataIndex: "outstandingBalance",
              width: 120,
              render: (amount) => (
                <Text
                  strong
                  style={{ color: amount > 0 ? "#ff4d4f" : "#52c41a" }}
                >
                  R {amount.toFixed(2)}
                </Text>
              ),
            },
            {
              title: "Days Overdue",
              dataIndex: "daysOverdue",
              width: 100,
              render: (days) => (
                <Tag color={getStatusColor(days)} size="small">
                  {days > 0 ? `${days}d` : "Current"}
                </Tag>
              ),
            },
            {
              title: "Payments",
              dataIndex: "paymentDetails",
              render: (_, record) => {
                const payments = record.paymentDetails;
                if (payments && payments.length > 0) {
                  return (
                    <div>
                      {payments.map((payment, index) => (
                        <Tag key={index} size="small" style={{ margin: "2px" }}>
                          R {payment.payAmount.toFixed(2)} (
                          {payment.paymentMode})
                        </Tag>
                      ))}
                    </div>
                  );
                }
                // Show aggregated amount if provided by API
                if (
                  typeof record.totalReceivedAmount === "number" &&
                  record.totalReceivedAmount > 0
                ) {
                  return (
                    <Tag color="blue" size="small">
                      R {record.totalReceivedAmount.toFixed(2)} received
                    </Tag>
                  );
                }
                return (
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    No payments
                  </Text>
                );
              },
            },
          ]}
          dataSource={record.invoices}
          pagination={false}
          size="small"
        />
      </div>
    );
  };

  // Invoice expandable row render (simplified version)
  const invoiceExpandedRowRender = (record) => {
    return (
      <div style={{ padding: "16px", backgroundColor: "#fafafa" }}>
        <Row gutter={16}>
          <Col span={12}>
            <Descriptions title="Customer Info" column={1} size="small">
              <Descriptions.Item label="Phone">
                {record.customerPhone}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {record.customerEmail}
              </Descriptions.Item>
              <Descriptions.Item label="Credit Limit">
                R {record.creditLimit.toFixed(2)}
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col span={12}>
            <Title level={5}>Payment History</Title>
            {record.paymentDetails && record.paymentDetails.length > 0 ? (
              record.paymentDetails.map((payment, index) => (
                <Tag
                  key={index}
                  style={{
                    margin: "4px",
                    display: "block",
                    width: "fit-content",
                  }}
                >
                  R {payment.payAmount.toFixed(2)} - {payment.paymentMode}
                  {payment.date && ` (${formatDate(payment.date)})`}
                </Tag>
              ))
            ) : record.totalReceivedAmount > 0 ? (
              <Tag
                style={{
                  margin: "4px",
                  display: "block",
                  width: "fit-content",
                }}
              >
                R {record.totalReceivedAmount.toFixed(2)} received
              </Tag>
            ) : (
              <Text type="secondary">No payments recorded</Text>
            )}
          </Col>
        </Row>
      </div>
    );
  };

  // Calculate summary statistics
  const summaryStats = React.useMemo(() => {
    const dataToUse =
      viewMode === "customer"
        ? customerGroupedData.reduce(
            (acc, customer) => [...acc, ...customer.invoices],
            []
          )
        : processedData;

    if (!dataToUse.length) return null;

    const totalOutstanding = dataToUse.reduce(
      (sum, item) => sum + item.outstandingBalance,
      0
    );
    const totalInvoiced = dataToUse.reduce(
      (sum, item) => sum + item.invoiceAmount,
      0
    );

    const aging = dataToUse.reduce(
      (acc, item) => {
        if (item.daysOverdue <= 0) acc.current += item.outstandingBalance;
        else if (item.daysOverdue <= 30)
          acc.thirtyDays += item.outstandingBalance;
        else if (item.daysOverdue <= 60)
          acc.sixtyDays += item.outstandingBalance;
        else acc.over90Days += item.outstandingBalance;
        return acc;
      },
      { current: 0, thirtyDays: 0, sixtyDays: 0, over90Days: 0 }
    );

    return {
      totalCustomers:
        viewMode === "customer"
          ? customerGroupedData.length
          : new Set(dataToUse.map((item) => item.customerId)).size,
      totalInvoices: dataToUse.length,
      totalInvoiced,
      totalOutstanding,
      ...aging,
    };
  }, [processedData, customerGroupedData, viewMode]);

  useEffect(() => {
    if (user && !isDropdownPermission(user.role_Id)) {
      setOutletFilter(user.outletID);
    }
  }, [user]);

  const currentData =
    viewMode === "customer" ? customerGroupedData : processedData;
  const currentColumns =
    viewMode === "customer" ? customerColumns : invoiceColumns;
  const currentExpandedRowRender =
    viewMode === "customer"
      ? customerExpandedRowRender
      : invoiceExpandedRowRender;

  // Export grouped customer/invoice data to CSV with aging buckets
  const exportToXLSX = () => {
    try {
      // Always use grouped structure to satisfy requirement of blank customer name for subsequent invoices
      const grouped = customerGroupedData;
      const rows = [
        [
          "Customer Name",
          "Invoice Number",
          "Date",
          "Due Date",
          "Amount",
          "Outstanding",
          "Payments",
          "Current",
          "1-30 Days",
          "31-60 Days",
          "90+ Days",
        ],
      ];

      grouped.forEach((customer) => {
        // Pre-compute aging per invoice
        customer.invoices.forEach((inv, idx) => {
          const days = inv.daysOverdue;
          const agingCols = {
            current: 0,
            thirty: 0,
            sixty: 0,
            over90: 0,
          };
          if (days <= 0) agingCols.current = inv.outstandingBalance;
          else if (days <= 30) agingCols.thirty = inv.outstandingBalance;
          else if (days <= 60) agingCols.sixty = inv.outstandingBalance;
          else agingCols.over90 = inv.outstandingBalance;

          // Payments formatting
          let paymentsStr = "";
          if (inv.paymentDetails && inv.paymentDetails.length) {
            paymentsStr = inv.paymentDetails
              .map(
                (p) =>
                  `R ${Number(p.payAmount).toFixed(2)} ${p.paymentMode || ""}$${
                    p.date ? " " + formatDate(p.date) : ""
                  }`
              )
              .join("; ");
          } else if (
            typeof inv.totalReceivedAmount === "number" &&
            inv.totalReceivedAmount > 0
          ) {
            paymentsStr = `R ${inv.totalReceivedAmount.toFixed(2)} received`;
          }

          const row = [
            idx === 0 ? customer.customerName : "", // only first invoice row shows customer name
            inv.invoiceNumber,
            formatDate(inv.invoiceDate),
            formatDate(inv.dueDate),
            `R ${inv.invoiceAmount.toFixed(2)}`,
            `R ${inv.outstandingBalance.toFixed(2)}`,
            paymentsStr,
            agingCols.current ? `R ${agingCols.current.toFixed(2)}` : "",
            agingCols.thirty ? `R ${agingCols.thirty.toFixed(2)}` : "",
            agingCols.sixty ? `R ${agingCols.sixty.toFixed(2)}` : "",
            agingCols.over90 ? `R ${agingCols.over90.toFixed(2)}` : "",
          ];
          rows.push(row);
        });
      });

      const worksheet = XLSX.utils.aoa_to_sheet(rows);
      // Auto width
      const colWidths = rows[0].map((_, colIndex) => {
        let maxLen = 10;
        rows.forEach((r) => {
          const val = r[colIndex] ? String(r[colIndex]) : "";
          if (val.length > maxLen) maxLen = val.length;
        });
        return { wch: Math.min(maxLen + 2, 40) };
      });
      worksheet["!cols"] = colWidths;
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, worksheet, "CreditReport");
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      XLSX.writeFile(wb, `credit-report-${ts}.xlsx`);
    } catch (e) {
      console.error("Failed to export XLSX", e);
    }
  };

  // Helper function to calculate aging amounts for a single invoice
  const getAgingAmounts = (invoice) => {
    const days = invoice.daysOverdue;
    const agingCols = {
      current: 0,
      thirty: 0,
      sixty: 0,
      over90: 0,
    };
    if (days <= 0) agingCols.current = invoice.outstandingBalance;
    else if (days <= 30) agingCols.thirty = invoice.outstandingBalance;
    else if (days <= 60) agingCols.sixty = invoice.outstandingBalance;
    else agingCols.over90 = invoice.outstandingBalance;
    return agingCols;
  };

  // Export grouped customer/invoice data to PDF
  const exportToPDF = () => {
    try {
      const grouped = customerGroupedData;
      // summaryStats is already calculated above using React.useMemo

      // Prepare PDF columns
      const columns = [
        "Customer Name",
        "Invoice Number",
        "Date",
        "Due Date",
        "Amount",
        "Outstanding",
        "Payments",
        "Current",
        "30 Days",
        "60 Days",
        "90+ Days",
      ];

      // Prepare PDF rows
      const rows = [];
      grouped.forEach((customer) => {
        customer.invoices.forEach((inv, idx) => {
          const agingCols = getAgingAmounts(inv);
          let paymentsStr = "No payments";
          if (
            inv.paymentDetails &&
            inv.paymentDetails.length > 0 &&
            inv.totalReceivedAmount > 0
          ) {
            paymentsStr = `R ${inv.totalReceivedAmount.toFixed(2)} received`;
          }

          const row = [
            idx === 0 ? customer.customerName : "", // only first invoice row shows customer name
            inv.invoiceNumber,
            formatDate(inv.invoiceDate),
            formatDate(inv.dueDate),
            `R ${inv.invoiceAmount.toFixed(2)}`,
            `R ${inv.outstandingBalance.toFixed(2)}`,
            paymentsStr,
            agingCols.current ? `R ${agingCols.current.toFixed(2)}` : "",
            agingCols.thirty ? `R ${agingCols.thirty.toFixed(2)}` : "",
            agingCols.sixty ? `R ${agingCols.sixty.toFixed(2)}` : "",
            agingCols.over90 ? `R ${agingCols.over90.toFixed(2)}` : "",
          ];
          rows.push(row);
        });
      });

      // Calculate total values for summary
      const totalCreditLimit = grouped.reduce(
        (sum, customer) => sum + customer.creditLimit,
        0
      );
      const totalCustomers = grouped.length;

      downloadPDF({
        title: "Credit Report",
        docName: `Credit_Report_${new Date().toISOString().slice(0, 10)}`,
        startDate: dateRange?.[0] ? dateRange[0].format("YYYY-MM-DD") : "",
        endDate: dateRange?.[1] ? dateRange[1].format("YYYY-MM-DD") : "",
        columns,
        rows,
        totalOutstanding: summaryStats.totalOutstanding.toFixed(2),
        totalInvoiced: summaryStats.totalInvoiced.toFixed(2),
        totalCreditLimit: totalCreditLimit.toFixed(2),
        totalCustomers: totalCustomers,
        currentAmount: summaryStats.current.toFixed(2),
        thirtyDaysAmount: summaryStats.thirtyDays.toFixed(2),
        sixtyDaysAmount: summaryStats.sixtyDays.toFixed(2),
        over90DaysAmount: summaryStats.over90Days.toFixed(2),
      });
    } catch (e) {
      console.error("Failed to export PDF", e);
    }
  };

  return (
    <div>
      <Row align="middle" justify="space-between" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={4} style={{ color: "white", margin: 0 }}>
            Credit Manager Report
          </Title>
        </Col>
      </Row>

      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8}>
            <label style={{ display: "block", marginBottom: 8 }}>
              Date Range (Check Out Date)
            </label>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              style={{ width: "100%" }}
              suffixIcon={<CalendarOutlined />}
              placeholder={["Start Date", "End Date"]}
              allowClear
            />
          </Col>

          <Col xs={24} sm={12} md={4}>
            <label style={{ display: "block", marginBottom: 8 }}>
              Customer/Invoice
            </label>
            <Input
              placeholder="Search customer or invoice"
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              prefix={<SearchOutlined />}
              allowClear
            />
          </Col>

          <Col xs={24} sm={12} md={4}>
            <label style={{ display: "block", marginBottom: 8 }}>Status</label>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: "100%" }}
              placeholder="All Status"
              allowClear
            >
              <Option value="Current">Current</Option>
              <Option value="1-30 Days">1-30 Days</Option>
              <Option value="31-60 Days">31-60 Days</Option>
              <Option value="Over 90 Days">Over 90 Days</Option>
            </Select>
          </Col>

          <Col xs={24} sm={12} md={4}>
            <label style={{ display: "block", marginBottom: 8 }}>Outlet</label>
            <Select
              value={outletFilter}
              onChange={setOutletFilter}
              style={{ width: "100%" }}
              placeholder="Select Outlet"
              allowClear
            >
              {outlets.map((outlet) => (
                <Option
                  key={outlet.outletID || outlet.id}
                  value={String(outlet.outletID || outlet.id)}
                >
                  {outlet.outletName || outlet.name}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={4}>
            <label style={{ display: "block", marginBottom: 8 }}>View</label>
            <Select
              value={viewMode}
              onChange={setViewMode}
              style={{ width: "100%" }}
            >
              <Option value="customer">
                <UserOutlined /> Customer View
              </Option>
              <Option value="invoice">Invoice View</Option>
            </Select>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col>
            <Space>
              <Button
                type="btn btn-success me-2"
                disabled={
                  isLoading || loadingData || !customerGroupedData.length
                }
                onClick={exportToPDF}
                style={{ marginRight: 8 }}
              >
                Download PDF <FaRegFilePdf size={15} />
              </Button>
              <Button
                type="btn btn-warning me-2"
                disabled={
                  isLoading || loadingData || !customerGroupedData.length
                }
                onClick={exportToXLSX}
              >
                Download Excel <FaFileExcel size={15} />
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card>
        {(isLoading || loadingData) && (
          <div style={{ textAlign: "center", padding: "50px" }}>Loading...</div>
        )}

        {!isLoading && !loadingData && (
          <>
            {loadError && (
              <Alert
                type="error"
                message={loadError}
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
            {summaryStats && (
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={12} sm={8} md={4}>
                  <Card size="small" style={{ textAlign: "center" }}>
                    <Text type="secondary">
                      {viewMode === "customer"
                        ? "Total Customers"
                        : "Unique Customers"}
                    </Text>
                    <div>
                      <Text strong style={{ fontSize: 18 }}>
                        {summaryStats.totalCustomers}
                      </Text>
                    </div>
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Card size="small" style={{ textAlign: "center" }}>
                    <Text type="secondary">Total Invoices</Text>
                    <div>
                      <Text strong style={{ fontSize: 18 }}>
                        {summaryStats.totalInvoices}
                      </Text>
                    </div>
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Card size="small" style={{ textAlign: "center" }}>
                    <Text type="secondary">Total Invoiced</Text>
                    <div>
                      <Text strong style={{ fontSize: 16, color: "#1890ff" }}>
                        R {summaryStats.totalInvoiced.toFixed(2)}
                      </Text>
                    </div>
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Card size="small" style={{ textAlign: "center" }}>
                    <Text type="secondary" style={{ whiteSpace: "nowrap" }}>
                      Total Outstanding
                    </Text>
                    <div>
                      <Text strong style={{ fontSize: 16, color: "#ff4d4f" }}>
                        R {summaryStats.totalOutstanding.toFixed(2)}
                      </Text>
                    </div>
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={3}>
                  <Card
                    size="small"
                    style={{ textAlign: "center", backgroundColor: "#f6ffed" }}
                  >
                    <Text type="secondary">Current</Text>
                    <div>
                      <Text strong style={{ fontSize: 14, color: "#52c41a" }}>
                        R {summaryStats.current.toFixed(2)}
                      </Text>
                    </div>
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={3}>
                  <Card
                    size="small"
                    style={{ textAlign: "center", backgroundColor: "#fffbe6" }}
                  >
                    <Text type="secondary">30 Days</Text>
                    <div>
                      <Text strong style={{ fontSize: 14, color: "#faad14" }}>
                        R {summaryStats.thirtyDays.toFixed(2)}
                      </Text>
                    </div>
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={3}>
                  <Card
                    size="small"
                    style={{ textAlign: "center", backgroundColor: "#fff2e8" }}
                  >
                    <Text type="secondary">60 Days</Text>
                    <div>
                      <Text strong style={{ fontSize: 14, color: "#ff7a45" }}>
                        R {summaryStats.sixtyDays.toFixed(2)}
                      </Text>
                    </div>
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={3}>
                  <Card
                    size="small"
                    style={{ textAlign: "center", backgroundColor: "#fff1f0" }}
                  >
                    <Text type="secondary">90+ Days</Text>
                    <div>
                      <Text strong style={{ fontSize: 14, color: "#ff4d4f" }}>
                        R {summaryStats.over90Days.toFixed(2)}
                      </Text>
                    </div>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Risk Alerts */}
            {viewMode === "customer" && customerGroupedData.length > 0 && (
              <Row style={{ marginBottom: 16 }}>
                <Col span={24}>
                  {customerGroupedData.some(
                    (c) => c.totalOutstanding > c.creditLimit
                  ) && (
                    <Alert
                      message="Credit Limit Exceeded"
                      description={`${
                        customerGroupedData.filter(
                          (c) => c.totalOutstanding > c.creditLimit
                        ).length
                      } customer(s) have exceeded their credit limit.`}
                      type="error"
                      icon={<ExclamationCircleOutlined />}
                      showIcon
                      style={{ marginBottom: 8 }}
                    />
                  )}
                  {customerGroupedData.some((c) => c.maxDaysOverdue > 60) && (
                    <Alert
                      message="High Risk Customers"
                      description={`${
                        customerGroupedData.filter((c) => c.maxDaysOverdue > 60)
                          .length
                      } customer(s) have invoices overdue by more than 60 days.`}
                      type="warning"
                      icon={<ExclamationCircleOutlined />}
                      showIcon
                      style={{ marginBottom: 8 }}
                    />
                  )}
                </Col>
              </Row>
            )}

            <Table
              columns={currentColumns}
              dataSource={currentData}
              expandable={{
                expandedRowRender: currentExpandedRowRender,
                expandRowByClick: true,
                rowExpandable: (record) =>
                  viewMode === "customer"
                    ? record.invoices.length > 0
                    : record.outstandingBalance > 0,
              }}
              pagination={{
                pageSize: viewMode === "customer" ? 20 : 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} ${
                    viewMode === "customer" ? "customers" : "invoices"
                  }`,
              }}
              scroll={{ x: viewMode === "customer" ? 900 : 1200 }}
              size="small"
              rowClassName={(record) => {
                if (viewMode === "customer") {
                  if (record.totalOutstanding > record.creditLimit)
                    return "credit-over-limit";
                  if (record.maxDaysOverdue > 60) return "high-risk-customer";
                  if (record.maxDaysOverdue > 30) return "medium-risk-customer";
                } else {
                  if (record.daysOverdue > 60) return "high-risk-invoice";
                  if (record.daysOverdue > 30) return "medium-risk-invoice";
                }
                return "";
              }}
            />
          </>
        )}
      </Card>

      <style jsx>{`
        .credit-over-limit {
          background-color: #fff1f0 !important;
        }
        .high-risk-customer,
        .high-risk-invoice {
          background-color: #fff2e8 !important;
        }
        .medium-risk-customer,
        .medium-risk-invoice {
          background-color: #fffbe6 !important;
        }
      `}</style>
    </div>
  );
};

export default CreditManagerReport;
